import {
  isProportionFactKey,
  isProportionUnit,
  unitClassifiesStorageForm,
} from "@atlas/computation";

/**
 * True when a value is stored as a 0–1 fraction and should therefore be shown,
 * and typed, as a percent.
 *
 * `unit`/`format` are free text authored by the model, so an assumption can be
 * a proportion while describing itself in prose ("share of freed physician
 * time converted to realized cost savings"). Pass `id` wherever it is known —
 * an assumption's `a.<id>`, a fact's `c.<key>`: where the unit states nothing,
 * the name shape classifies it, so it renders as `50%` and the editor divides
 * the typed `50` by 100 instead of storing a raw `50` that scales its stream
 * 100×. Name classification is shared with the projection guards
 * (`isProportionAssumption`) so display, editing, and validation cannot
 * disagree about what a value means.
 *
 * The name is the LAST signal consulted, never the first. A unit that
 * classifies the storage form on its own — money, a stated proportion, or any
 * dimension — settles it, and only a unit that says nothing (blank, `number`,
 * or a bare `rate`/`ratio`) hands the question to the name.
 */
export function isPercentLike(assumption: {
  /** The name that classifies the value: `a.<id>` or `c.<key>`. Omit for
   *  ad-hoc values that have neither. */
  id?: string | null;
  format?: string | null;
  unit?: string | null;
}): boolean {
  const format = (assumption.format ?? "").trim().toLowerCase();
  const unit = (assumption.unit ?? "").trim().toLowerCase();
  if (
    format === "percent" ||
    format === "percentage" ||
    format === "percent (decimal)" ||
    unit === "percent" ||
    unit === "percentage" ||
    unit === "%" ||
    unit === "percent (decimal)"
  ) {
    return true;
  }
  // A declared money unit settles it before any heuristic runs: a
  // `referral_capture_value` in dollars must render "$250", never "25000%".
  if (isCurrency(assumption)) return false;
  // A prose unit that states a part-of-whole ("fraction of ...") is decisive
  // on its own; the name shape only applies where an id is known.
  if (isProportionUnit(assumption.unit)) return true;
  // A unit that states a DIMENSION has ruled the convention out, and outranks
  // any name. `error_rate_per_1000` in "events/1,000 encounters" holds 4 events
  // per thousand: read from its name alone it renders "400%", prefills the
  // editor with 400, and writes 0.04 back when the reader retypes what they
  // were shown. The name is the second signal for a unit that states nothing —
  // blank, `number`, or a bare `rate`/`ratio` — and only for those.
  if (unitClassifiesStorageForm(assumption.unit)) return false;
  return assumption.id ? isProportionFactKey(assumption.id) : false;
}

function formatPercent(value: number): string {
  const percent = value * 100;
  return Number.isInteger(percent)
    ? percent.toFixed(0)
    : percent.toFixed(2).replace(/\.?0+$/, "");
}

export function isCurrency(assumption: {
  format?: string | null;
  unit?: string | null;
}): boolean {
  const normalizedUnit = (assumption.unit ?? "").trim().toLowerCase();
  const normalizedFormat = (assumption.format ?? "").trim().toLowerCase();
  return (
    normalizedFormat === "currency" ||
    normalizedUnit === "$" ||
    normalizedUnit === "currency" ||
    normalizedUnit === "usd" ||
    normalizedUnit === "dollars" ||
    normalizedUnit.startsWith("$/")
  );
}

export function currencyUnitSuffix(unit: string | null): string {
  const rawUnit = unit?.trim() ?? "";
  const normalizedUnit = rawUnit.toLowerCase();
  if (normalizedUnit === "$/month") return "/mo";
  if (normalizedUnit === "$/minute") return "/min";
  if (rawUnit.startsWith("$/")) return rawUnit.slice(1);

  const basis = rawUnit.match(
    /^(?:usd|us\$|\$|dollars?)\s*(?:\/|per\s+)(.+)$/i,
  )?.[1];
  if (!basis) return "";
  return `/${basis
    .trim()
    .replace(/\s+per\s+/gi, "/")
    .replace(/\s+/g, " ")}`;
}

const COMPACT_USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  notation: "compact",
  maximumFractionDigits: 1,
  minimumFractionDigits: 0,
});

/** Full (non-abbreviated) currency, grouped with commas. Used for the
 *  assumption detail rail, where a shorthand like "$10K" would hide the
 *  exact value being edited. */
const FULL_USD = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
  minimumFractionDigits: 0,
});

const USD_CENTS = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const GROUPED_NUMBER = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 3,
});

export interface FormatAssumptionNumOptions {
  /** Abbreviate large currency amounts ("$10K", "$1.2M"). Defaults to `true`
   *  for table/summary surfaces; pass `false` for the detail rail, where the
   *  exact value should always be visible. Only currency is affected — every
   *  other format is already a full number. */
  compact?: boolean;
  /** The name that classifies the value: an assumption's `a.<id>` or a fact's
   *  `c.<key>`. Lets a proportion whose unit states no convention still render
   *  as a percent. Consulted only when the unit settles nothing — a declared
   *  dimension outranks it. */
  id?: string | null;
}

export function formatAssumptionNum(
  value: number,
  unit: string | null,
  format?: string | null,
  opts?: FormatAssumptionNumOptions,
): string {
  if (isPercentLike({ id: opts?.id, format, unit }))
    return `${formatPercent(value)}%`;
  if (isCurrency({ unit, format })) {
    const compact = opts?.compact ?? true;
    const formatted =
      Math.abs(value) < 1
        ? USD_CENTS.format(value)
        : compact
          ? COMPACT_USD.format(value)
          : FULL_USD.format(value);
    return `${formatted}${currencyUnitSuffix(unit)}`;
  }
  if (unit === "minutes") return `${value.toFixed(1)} min`;
  return GROUPED_NUMBER.format(value);
}
